import React from 'react';

export type Language = 'en' | 'sv';
export const internationalText = {
  resource: { en: 'en', sv: 'sv' },
  menu: {
    market_demand: { en: 'Market demand', sv: 'Efterfrågan' },
    search_behaviour: { en: 'Search behaviour', sv: 'Sökbeteende' },
    client_insights: { en: 'Site insights', sv: 'Siteinsikter' },
    client_potential: { en: 'Site potential', sv: 'Sitepotential' },
    competitive_insights: { en: 'Competitive insights', sv: 'Konkurrentinsikter' },
  },
  annotation: {
    tag_group: { en: 'Topic', sv: 'Huvudämne' },
    tag: { en: 'Sub topic', sv: 'Underämne' },
    baseline_ctr_avg: { en: 'Baseline CTR', sv: 'Ursprunglig CTR' },
    target_ctr_avg: { en: 'Target CTR', sv: 'Mål CTR' },
    ramp_up_time: { en: 'Ramp up time', sv: 'Implementeringstid' },
    year_trend: { en: '{0} year trend', sv: '{0} års trend' },
    conversion_rate: { en: 'Conversion rate', sv: 'Konverteringsgrad' },
    average_order_value: { en: 'Average order value', sv: 'Snittorder' },
  },
  chart: {
    market_demand: {
      title: { en: 'Market demand', sv: 'Efterfrågan' },
      subtitle: {
        en: "What's the overall market potential?",
        sv: 'Hur stor är efterfrågan på marknaden?',
      },
      keywords: {
        label: { en: 'keywords', sv: 'sökord' },
        description: {
          en: 'We combine our experience in search with industry knowledge and the tools at our disposal to find a comprehensive list of relevant keywords',
          sv: 'Vi kombinerar vår erfarenhet inom sök med industrierfarenhet och vårt arbetssätt för att hitta en omfattande lista av relevanta sökord',
        },
      },
      searches: {
        label: { en: 'monthly searches', sv: 'sökningar per månad' },
        description: {
          en: 'Using the list we identify search patterns. The list is then run through Google Ads API to determine market demand',
          sv: 'Utifrån listan identifierar vi sökbeteenden. Listan körs sedan genom Google Ads API:t för att bestämma efterfrågan',
        },
      },
      traffic: {
        label: { en: 'traffic potential', sv: 'trafikpotential' },
        description: {
          en: 'Based on market demand we estimate max traffic potential by combining search volume with average click-through-rates for the top-ranked page',
          sv: 'Baserat på efterfrågan estimerar vi den maximala trafikpotentialen genom att kombinera sökvolym med genomsnittlig CTR för en topprankad sida',
        },
      },
    },
    demand_by_month: {
      title: { en: 'Demand by month', sv: 'Efterfrågan per månad' },
      subtitle: {
        en: 'Is the search behaviour seasonal?',
        sv: 'Finns det säsongsvariationer i sökbeteendet?',
      },
      month_1: { en: 'Jan', sv: 'Jan' },
      month_2: { en: 'Feb', sv: 'Feb' },
      month_3: { en: 'Mar', sv: 'Mar' },
      month_4: { en: 'Apr', sv: 'Apr' },
      month_5: { en: 'May', sv: 'Maj' },
      month_6: { en: 'Jun', sv: 'Jun' },
      month_7: { en: 'Jul', sv: 'Jul' },
      month_8: { en: 'Aug', sv: 'Aug' },
      month_9: { en: 'Sep', sv: 'Sep' },
      month_10: { en: 'Oct', sv: 'Okt' },
      month_11: { en: 'Nov', sv: 'Nov' },
      month_12: { en: 'Dec', sv: 'Dec' },
    },
    demand_trend: {
      title: { en: 'Demand trend', sv: 'Trend' },
      subtitle: { en: 'Is the search behaviour changing?', sv: 'Förändras sökbeteendet?' },
    },
    search_volume_distribution: {
      title: { en: 'Search demand distribution', sv: 'Fördelning av efterfrågan' },
      subtitle: {
        en: 'How are the keywords distributed after the volume intervals?',
        sv: 'Hur fördelas sökorden efter volymintervallerna?',
      },
      label_vol_interval: { en: 'Volume interval', sv: 'Volymintervall' },
      label_vol_sum: { en: 'Volume sum', sv: 'Volym summa' },
      interval_1: { en: 'Very small', sv: 'Väldigt liten' },
      interval_2: { en: 'Small', sv: 'Liten' },
      interval_3: { en: 'Medium', sv: 'Mellan' },
      interval_4: { en: 'Large', sv: 'Stor' },
      label_key_count: { en: 'Keyword count', sv: 'Sökord antal' },
    },
    demand_by_topic: {
      title: { en: 'Demand by main topic', sv: 'Efterfrågan per huvudämne' },
      subtitle: {
        en: 'What main topics are users searching for?',
        sv: 'Vilka huvudämnen söker användare efter?',
      },
      footnote: {
        en: 'Aggregated monthly search volume for all keywords in selected tag groups. One keyword can be in multiple tag groups',
        sv: 'Aggregerad månatlig sökvolym för alla sökord i utvalda taggrupper. Ett enskilt sökord kan finnas i flertalet taggrupper',
      },
      topics: { en: 'Main topics', sv: 'Huvudämnen' },
      volume: { en: 'Volume', sv: 'Sökvolym' },
      legend: { en: 'Volume', sv: 'Sökvolym' },
    },
    topic_combinations: {
      title: { en: 'Main topic combinations', sv: 'Kombinerade huvudämnen' },
      subtitle: {
        en: 'What combined topics are users searching for?',
        sv: 'Vilka kombinationer av huvudämnen söker användare efter?',
      },
      keyword_description: { en: '% of all keywords', sv: '% av alla sökord' },
      volume_description: { en: '% of total search volume', sv: '% av total sökvolym' },
    },
    keyword_cluster_by_demand: {
      title: { en: 'Demand by sub topic', sv: 'Efterfrågan per underämne' },
      subtitle: {
        en: 'What sub topics are users searching for?',
        sv: 'Vilka underämnen söker användare efter?',
      },
      footnote: {
        en: 'Aggregated monthly search volume for all keywords in each tag within selected tag groups',
        sv: 'Aggregerad sökvolym för alla sökord i varje tagg inom utvalda taggrupper',
      },
      legend: { en: 'Volume', sv: 'Sökvolym' },
    },
    topic_keyword_demand: {
      title: { en: 'Main topic keyword demand', sv: 'Sökordsefterfrågan per huvudämne' },
      subtitle: {
        en: 'What keywords has the most search demand within selected main topics and what % of the total search demand does each main topic have?',
        sv: 'Vilka sökord har mest sökvolym inom de valda huvudämnena och vilken %-andel av totala efterfrågan har varje huvudämne?',
      },
      footnote: {
        en: 'Monthly search volume per keyword and percentage of total search volume within the project for the tag groups',
        sv: 'Månatlig sökvolym per sökord och andel av total sökvolym inom projektet för taggrupperna',
      },
    },
    sub_topic_demand: {
      title: { en: 'Sub topic demand', sv: 'Efterfrågan per underämne' },
      subtitle: {
        en: 'What sub topics has the most demand within selected main topics and what % of the total search demand does each main topic have?',
        sv: 'Vilka underämnen har mest sökvolym inom de valda huvudämnena och vilken %-andel av totala efterfrågan har varje huvudämne?',
      },
      footnote: {
        en: 'Aggregated monthly search volume per tag and percentage of total search volume within the project for the tag groups',
        sv: 'Aggregerad månatlig sökvolym per tag och andel av total sökvolym inom projektet för taggrupperna',
      },
    },
    sub_topic_keyword_demand: {
      title: { en: 'Sub topic keyword demand', sv: 'Sökordsefterfrågan per underämne' },
      subtitle: {
        en: 'What keywords has the most demand within selected sub topics, and what % of the topics total  search demand does each sub topic have?',
        sv: 'Vilka sökord har mest sökvolym inom de valda underämnena och vilken %-andel av totala efterfrågan har varje underämne?',
      },
      footnote: {
        en: 'Monthly search volume per keyword within a tag and percentage of total search volume within the tag group for the tag',
        sv: 'Månatlig sökvolym per sökord inom en tag och andel av total sökvolym inom taggruppen för taggen',
      },
    },
    keyword_specificity: {
      title: { en: 'Keyword specificity', sv: 'Detaljerade sökningar' },
      subtitle: {
        en: 'How specific are users when they search?',
        sv: 'Hur detaljerade är användare när de söker?',
      },
      footnote: {
        en: 'Aggregated monthly search volume separated into specificity groups. Based on count of spaces in keywords, therefore the insights received is very simplified',
        sv: 'Aggregerad månatlig sökvolym separerat i mer specifika grupper. Grupperna är baserade på antalet mellanslag i sökord, därför är insikterna något förenklade',
      },
      head_desc: {
        en: 'Head - Low number of search terms but with a high demand',
        sv: 'Head - Lågt antal sökord med hög sökvolym',
      },
      body_desc: {
        en: 'Body - Medium number of search terms with a medium demand',
        sv: 'Body - Mellan antal sökord med mellan sökvolym',
      },
      longtail_desc: {
        en: 'Long tail - High number of search terms with a low demand',
        sv: 'Long tail - Högt antal sökord med låg sökvolym',
      },
      word: { en: 'word', sv: 'ord' },
      words: { en: 'words', sv: 'ord' },
    },
    keyword_specificity_by_topic: {
      title: { en: 'Keyword specificity by main topic', sv: 'Detaljerade sökningar på huvudämne' },
      subtitle: {
        en: 'How does keyword specificity differ within the topics?',
        sv: 'Hur detaljerade är användare när de söker inom huvudämnena? ',
      },
      footnote: {
        en: 'Aggregated monthly search volume separated into specificity groups per tag group. Based on count of spaces in keywords, therefore the insights received is very simplified',
        sv: 'Aggregerad månatlig sökvolym separerat i mer specifika grupper. Grupperna är baserade på antalet mellanslag i sökord, därför är insikterna något förenklade',
      },
      legend: { en: 'Volume', sv: 'Sökvolym' },
      word: { en: 'word', sv: 'ord' },
      words: { en: 'words', sv: 'ord' },
      head_keyword: { en: 'Head keyword', sv: 'Head sökord' },
      body_keyword: { en: 'Body keyword', sv: 'Body sökord' },
      longtail_keyword: { en: 'Long tail keyword', sv: 'Long tail sökord' },
    },
    ranking_distribution: {
      title: { en: 'Ranking distribution', sv: 'Positionsfördelning' },
      subtitle: {
        en: 'How many % of the keywords are within each rank interval?',
        sv: 'Hur många % av sökorden finns inom varje rankintervall?',
      },
    },
    demand_ranking_distribution: {
      title: { en: 'Demand ranking distribution', sv: 'Positionsfördelning efterfrågan' },
      subtitle: {
        en: 'How many % of the total search demand are within each rank interval?',
        sv: 'Hur många % av totala sökvolymen finns inom varje rankintervall?',
      },
    },
    traffic_ranking_distribution: {
      title: { en: 'Traffic ranking distribution', sv: 'Positionsfördelning trafik' },
      subtitle: {
        en: 'How many % of the total estimated traffic are within each rank interval?',
        sv: 'Hur många % av totala estimerade trafiken finns inom varje rankintervall?',
      },
    },
    topic_ranking_distribution: {
      title: { en: 'Main topic ranking distribution', sv: 'Positionsfördelning per huvudämne' },
      subtitle: {
        en: 'How many % of the keywords in the main topics are within each rank interval?',
        sv: 'Hur många % av sökorden i huvudämnena finns inom varje rankintervall?',
      },
      legend: { en: '% of keywords', sv: '% av antal sökord' },
    },
    topic_demand_ranking_distribution: {
      title: {
        en: 'Main topic demand ranking distribution',
        sv: 'Positionsfördelning efterfrågan per huvudämne',
      },
      subtitle: {
        en: 'How much search demand does main topics have within each rank interval?',
        sv: 'Hur mycket sökvolym har huvudämnena inom varje rankintervall?',
      },
      legend: { en: 'Volume', sv: 'Sökvolym' },
    },
    topic_traffic_ranking_distribution: {
      title: {
        en: 'Main topic traffic ranking distribution',
        sv: 'Positionsfördelning trafik per huvudämne',
      },
      subtitle: {
        en: 'How much estimated traffic does main topics have within each rank interval?',
        sv: 'Hur mycket estimerad trafik har huvudämnena inom varje rankintervall?',
      },
      legend: { en: 'Traffic', sv: 'Trafik' },
    },
    top_volume_keywords: {
      title: { en: 'Top volume keywords', sv: 'Sökord med störst efterfrågan per huvudämne' },
      subtitle: {
        en: 'What main topics have keywords with highest search demand?',
        sv: 'Vilka huvudämnen innehåller sökord med störst efterfrågan?',
      },
    },
    top_volume_keywords_and_rank: {
      title: {
        en: 'Top volume keywords and rank',
        sv: 'Ranking för sökord med störst efterfrågan per huvudämne',
      },
      subtitle: {
        en: 'How are the ranking distributed across the keywords with the highest search demand within the main topics?',
        sv: 'Hur ser positionsfördelningen ut för sökorden med störst efterfrågan inom huvudämnena?',
      },
    },
    best_pages_by_traffic: {
      title: { en: 'Best pages by traffic', sv: 'Bästa sidor sett till trafik' },
      subtitle: {
        en: 'What pages are currently getting the most estimated traffic?',
        sv: 'Vilka sidor har mest estimerad trafik?',
      },
      traffic: { en: 'Traffic', sv: 'Trafik' },
      page_url: { en: 'Page url', sv: 'URL' },
      best_keyword: { en: 'Best keyword', sv: 'Bästa sökord' },
      volume: { en: 'Volume', sv: 'Sökvolym' },
      keywords: { en: 'Keywords', sv: 'Sökord' },
      rank: { en: 'Rank', sv: 'Rank' },
    },
    performance: {
      title: { en: 'Performance', sv: 'Prestation' },
      subtitle: {
        en: 'How is the site performing in terms of its ranking for keywords in the top 10 search results?',
        sv: 'Hur presterar domänen idag i termer av ranking för sökord i top 10?',
      },
      rankings: {
        label: { en: 'Ranking', sv: 'Rankar' },
        number_label: { en: 'of keywords', sv: 'av sökorden' },
        desc_1: { en: '{0} is ranking top 10 for', sv: '{0} rankar top 10 på' },
        desc_2: { en: '{0} of {1}', sv: '{0} av {1}' },
        desc_3: { en: 'keywords', sv: 'sökord' },
      },
      reach: {
        label: { en: 'Visibility', sv: 'Synlighet' },
        number_label: { en: 'of searches', sv: 'av sökningar' },
        desc_1: { en: '{0} is visible in top 10 for', sv: '{0} är synlig i top 10 för' },
        desc_2: { en: '{0} of {1}', sv: '{0} av {1}' },
        desc_3: { en: 'monthly searches', sv: 'månatliga sökningar' },
      },
      traffic: {
        label: { en: 'Traffic', sv: 'Trafik' },
        number_label: { en: 'estimated clicks', sv: 'estimerade klick' },
        desc_1: { en: '{0} is estimated to get', sv: '{0} är estimerat att få' },
        desc_2: { en: '{0} of {1}', sv: '{0} av {1}' },
        desc_3: {
          en: 'potential traffic from all of the keywords in the project',
          sv: 'potentiell trafik från alla sökorden i projektet',
        },
      },
    },
    market_potential: {
      title: { en: 'Market potential', sv: 'Marknadspotential' },
      searches: { label: { en: 'Monthly searches', sv: 'Månatliga sökningar' } },
      traffic: {
        label: { en: 'Potential traffic', sv: 'Potentiell trafik' },
        desc_1: { en: 'Avg. rank {0}', sv: 'Snittrank {0}' },
        desc_2: { en: 'CTR {0}', sv: 'CTR {0}' },
        desc_3: { en: 'Current', sv: 'Nuvarande' },
      },
      conversions: {
        label: { en: 'Conversions', sv: 'Konverteringar' },
        desc_1: { en: 'CVR {0}', sv: 'CVR {0}' },
      },
      revenue: { desc_1: { en: 'AOV {0}', sv: 'AOV {0}' } },
    },
    market_potential_by_topic: {
      title: { en: 'Market potential by main topic', sv: 'Marknadspotential per huvudämne' },
      subtitle: {
        en: 'How much potential increase in traffic is there within the main topics?',
        sv: 'Hur stor potentiell ökning i trafik finns det inom de olika huvudämnena?',
      },
      current_traffic: { en: 'Current estimated traffic', sv: 'Nuvarande estimerad trafik' },
      traffic_increase: { en: 'Potential traffic increase', sv: 'Potentiell trafikökning' },
      traffic: { en: 'Traffic', sv: 'Trafik' },
    },
    market_potential_by_rank_improvement: {
      title: {
        en: 'Market potential by rank improvement',
        sv: 'Marknadspotential sett till positionsförbättringar',
      },
      subtitle: {
        en: 'How high is the search demand compared to current rank?',
        sv: 'Hur stor är efterfrågan jämfört med befintlig ranking?',
      },
      footnote: {
        en: (
          <span>
            The green columns show how many keywords our site is ranking for in each position. The
            dark blue line shows how much search demand there is for the keywords in the that
            position. Example of insight: our site ranks for 50 keywords in position three, but
            there is a low search demand for these keywords, which means that the site are most
            likely not gonna get that much traffic from these words.
            <br />
            <br />
            Example of insight There is a high search demand for the keywords in position 15, these
            could be keywords to pin point and start optimising for to improve the rank
            <br />
            <br />
            Example of insight: There are alot of keywords ranking in position 100+, there is also
            alot of search demand for these keywords. This often means that there is a lot of
            potential in new keywords to rank for that the site is not ranking for at all today.
          </span>
        ),
        sv: (
          <span>
            The green columns show how many keywords our site is ranking for in each position. The
            dark blue line shows how much search demand there is for the keywords in the that
            position. Example of insight: our site ranks for 50 keywords in position three, but
            there is a low search demand for these keywords, which means that the site are most
            likely not gonna get that much traffic from these words.
            <br />
            <br />
            Example of insight There is a high search demand for the keywords in position 15, these
            could be keywords to pin point and start optimising for to improve the rank
            <br />
            <br />
            Example of insight: There are alot of keywords ranking in position 100+, there is also
            alot of search demand for these keywords. This often means that there is a lot of
            potential in new keywords to rank for that the site is not ranking for at all today.
          </span>
        ),
      },
      search_desc: { en: 'Search demand', sv: 'Sökvolym' },
      vol_sum: { en: 'Sum of volume', sv: 'Summa av sökvolym' },
      rank: { en: 'Rank', sv: 'Rank' },
      legend_kw_count: { en: 'Number of ranking keywords', sv: 'Antal rankande sökord' },
      legend_volume: { en: 'Volume sum', sv: 'Total volym' },
    },
    traffic_cost: {
      title: { en: 'Accumulated traffic cost', sv: 'Ackumulerad trafikkostnad' },
      subtitle: {
        en: 'Investment to buy equivalent traffic via Google Ads',
        sv: 'Investering för att köpa motsvarande trafik via Google Ads',
      },
      footnote: {
        en: 'Yearly investment is calculated for each keyword as [Monthly clicks] x [Cost per click per month] x [12 months]. Google provides a low bid cost (20th percentile) and a high bid cost (80th percentile) for each keyword.',
        sv: 'Yearly investment is calculated for each keyword as [Monthly clicks] x [Cost per click per month] x [12 months]. Google provides a low bid cost (20th percentile) and a high bid cost (80th percentile) for each keyword.',
      },
      monthly_increase: { en: 'Monthly increase', sv: 'Månatlig ökning' },
      visits: { en: 'visits', sv: 'besök' },
      yearly_cost: { en: 'Yearly cost', sv: 'Årskostnad' },
      year: { en: 'Year', sv: 'År' },
      explanation: {
        en: 'Note that this is based on coarse assumptions, you would probably not buy this specific traffic on these search terms',
        sv: 'Notera att det här är baserat på grova antaganden, man skulle förmodligen inte köpa den här specifika trafiken på de här specifika söktermerna',
      },
    },
    competitive_landscape: {
      title: { en: 'Competitive landscape', sv: 'Positioneringskarta konkurrenter' },
      subtitle: {
        en: 'Who gets the most estimated traffic and who ranks top 10 for the most keywords?',
        sv: 'Vem har mest estimerad trafik och vem rankar topp 10 på flest sökord?',
      },
      kw_label: { en: 'Number of top 10 ranking keywords', sv: 'Antal topp-10-rankande sökord' },
      traffic_label: { en: 'Estimated monthly traffic', sv: 'Estimerad trafik i månaden' },
    },
    performance_vs_competitors: {
      title: { en: 'Performance vs competitors', sv: 'Prestation konkurrenter' },
      subtitle: {
        en: 'How are the competitors performing in terms of its ranking for keywords in the top 10 search results?',
        sv: 'Hur presterar konkurenterna i termer av deras ranking för sökord bland top 10 sökresultat?',
      },
      rankings: {
        label: { en: 'Ranking', sv: 'Rankar' },
        desc: { en: '% of {0} keywords in top 10', sv: '% av {0} sökord i top 10' },
      },
      visibility: {
        label: { en: 'Visibility', sv: 'Synlighet' },
        desc: { en: '% of {0} searches in top 10', sv: '% av {0} månatliga sökningar i top 10' },
      },
      traffic: {
        label: { en: 'Traffic', sv: 'Trafik' },
        desc: {
          en: '# of {0} estimated traffic for all keywords in project',
          sv: '# av {0} estimerad trafik för alla sökord i projektet',
        },
      },
    },
    best_competitor_keywords: {
      title: { en: 'Best competitor keywords', sv: 'Bästa sökord konkurrenter' },
      subtitle: {
        en: 'How much estimated traffic does the competitors get, and what are their best performing keywords?',
        sv: 'Hur mycket estimerad trafik får konkurrenterna och vilka sökord presterar bäst?',
      },
    },
    competitors_by_topic: {
      title: { en: 'Competitors by main topic', sv: 'Konkurrenter per huvudämne' },
      subtitle: {
        en: 'Who gets the most estimated traffic within the topics?',
        sv: 'Vem har mest estimerad trafik inom huvudämnena?',
      },
      client: { en: 'our site', sv: 'vår site' },
      good: { en: 'good', sv: 'bra' },
      bad: { en: 'bad', sv: 'sämre' },
    },
    competitive_heatmap: {
      title: { en: 'Competitive heatmap', sv: 'Heatmap konkurrenter' },
      subtitle: {
        en: 'Who ranks the best for the keywords with the highest search demand?',
        sv: 'Vem rankar bäst på sökorden med störst efterfrågan?',
      },
      keyword: { en: 'Keyword', sv: 'Sökord' },
      volume: { en: 'Volume', sv: 'Sökvolym' },
    },
    general: { more: { en: 'and {0} more', sv: 'och {0} till' } },
    traffic_forecast: {
      title: { en: 'Traffic forecast', sv: 'Trafikprognos' },
      subtitle: { en: '', sv: '' },
      legend_current_forecast: { en: 'Business as usual', sv: 'Business as usual' },
      legend_pixel_forecast: { en: 'Potential traffic', sv: 'Potentiell trafik' },
      footnote: {
        en: 'The change in traffic is based on an assumption of a linear increase/decrease in CTR in relation to actual CTR during the selected implementation period',
        sv: 'Förändringen i trafik baseras på ett antagande om en linjär ökning/minsking av CTR i förhållande till verklig CTR under vald implementeringstid',
      },
    },
    revenue_forecast: {
      title: { en: 'Revenue forecast', sv: 'Intäktsprognos' },
      subtitle: { en: '', sv: '' },
      legend_current_forecast: { en: 'Business as usual', sv: 'Business as usual' },
      legend_pixel_forecast: { en: 'Potential revenues', sv: 'Potentiella intäkter' },
      footnote: {
        en: 'The change in revenue is based on an assumption of a linear increase/decrease in CTR relative to actual CTR during the selected implementation period',
        sv: 'Förändringen av intäkter baseras på ett antagande om en linjär ökning/minsking av CTR i förhållande till verklig CTR under vald implementeringstid',
      },
    },
  },
  multi_chart: {
    ranking_distribution_volume: {
      title: { en: 'Ranking distribution by volume', sv: 'Rankfördelning, volymbaserad' },
      subtitle: {
        en: "What's our ranking performance across different markets?",
        sv: 'Hur presterar vi på olika marknader?',
      },

      volume: {
        en: 'Search volume',
        sv: 'Sökvolym',
      },
    },
    ranking_distribution_search_terms: {
      title: { en: 'Ranking distribution by search terms', sv: 'Rankfördelning, sökordsbaserad' },
      subtitle: {
        en: "What's our ranking performance across different markets?",
        sv: 'Hur presterar vi på olika marknader?',
      },
      search_terms: {
        en: '# Keywords',
        sv: '# Sökord',
      },
    },
  },
};
